/**
 * W4 CityGraph-native analytic life.
 *
 * Daily plans reuse the shipped role templates, but resolve anchors against W4
 * parcel provenance and route walks over the grown street graph. No TownDef,
 * mutable simulation state, or networked NPC position is involved.
 */
import * as pmath from "../pmath.js";
import {
  NPC_SCHEDULES, directionDayFrac, hash32, npcClockShiftForId,
  npcWalkSpeedForId, seedInt, type DaySegment, type NpcPose, type NpcScheduleSlot,
} from "../npcSchedule.js";
import type { SystemDef } from "../universe.js";
import {
  EDGE_TUNNEL, USE_ADMIN, USE_BASILICA, USE_CHAPEL, USE_HALL, USE_HEADMAN,
  USE_INN, USE_MARKET_HALL, USE_MARKET_SQ, USE_PALACE, USE_TEMPLE,
  USE_DEPOT, USE_APOTHECARY, USE_SMITHY, USE_WORKSHOP,
  USE_SHRINE, USE_BATHHOUSE,
  type CityGraph,
} from "./citygraph.js";
import type { RegionChronicle, SettlementChronicle } from "./collapse.js";
import { buildingFootprint } from "./structures.js";
import { interiorClassForUse } from "../buildings/interior.js";
import type { AgentRecord } from "./roster.js";

export interface RegionWalkPath {
  pts: Float64Array;
  cum: Float64Array;
  len: number;
}

const h01 = (h: number): number => h / 4294967296;
const CIVIC_USES = new Set([USE_HALL, USE_HEADMAN, USE_ADMIN, USE_INN, USE_MARKET_HALL, USE_CHAPEL, USE_TEMPLE, USE_BASILICA, USE_PALACE]);
// 2026-07-31 richer schedules: venue categories for errand/worship/tavern slots.
const MARKET_USES = new Set([USE_MARKET_HALL, USE_DEPOT, USE_APOTHECARY, USE_SMITHY, USE_WORKSHOP]);
const SHRINE_USES = new Set([USE_TEMPLE, USE_CHAPEL, USE_BASILICA, USE_SHRINE]);
const TAVERN_USES = new Set([USE_INN, USE_BATHHOUSE]);
const SOCIAL_TRAITS = new Set(["cheerful", "kind", "curious", "proud"]);

/** Trait-conditioned schedule variant from the adopted W4 design. Social
 *  residents favor public squares; restless residents turn the same leisure
 *  beat into a wider street walk. */
export function regionScheduleVariant(agent: AgentRecord, slot: NpcScheduleSlot): NpcScheduleSlot {
  if (slot.activity !== "social") return slot;
  if (agent.traits.includes("restless")) return { ...slot, activity: "stroll", anchor: "stroll" };
  if (agent.traits.some((trait) => SOCIAL_TRAITS.has(trait))) return { ...slot, anchor: "plaza" };
  return slot;
}

/** Maximum home-ranked street candidates considered for a stroll. */
export function regionStrollCandidateLimit(agent: AgentRecord): number {
  if (agent.traits.includes("restless")) return 24;
  if (agent.traits.includes("curious")) return 14;
  if (agent.traits.includes("weary") || agent.traits.includes("wary")) return 5;
  return 8;
}

function parcelRefs(settlement: SettlementChronicle): number[] {
  const refs = new Set<number>();
  for (const entry of settlement.entries) if (entry.kind === "district-founded") {
    for (const ref of entry.refs) refs.add(ref);
  }
  return [...refs].sort((a, b) => a - b);
}

function centroid(graph: CityGraph, parcel: number): [number, number] {
  const poly = graph.parcels[parcel].poly;
  let u = 0, v = 0;
  const count = poly.length / 2;
  for (let i = 0; i < poly.length; i += 2) { u += poly[i]; v += poly[i + 1]; }
  return count > 0 ? [u / count, v / count] : [0, 0];
}

/** Road-facing step immediately outside the derived building footprint. */
function parcelDoor(graph: CityGraph, parcel: number | null): [number, number] {
  if (parcel === null) return [0, 0];
  const par = graph.parcels[parcel];
  const fp = buildingFootprint(par.poly, par.use);
  const [cu, cv] = fp ? [fp.cu, fp.cv] : centroid(graph, parcel);
  let qu = cu, qv = cv, best = Infinity;
  for (let e = 0; e < graph.edgeA.length; e++) {
    if (graph.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = graph.edgeA[e], b = graph.edgeB[e];
    const au = graph.nodeU[a], av = graph.nodeV[a], du = graph.nodeU[b] - au, dv = graph.nodeV[b] - av;
    const l2 = du * du + dv * dv;
    let t = l2 > 1e-12 ? ((cu - au) * du + (cv - av) * dv) / l2 : 0;
    t = Math.max(0, Math.min(1, t));
    const u = au + du * t, v = av + dv * t;
    const d2 = (u - cu) * (u - cu) + (v - cv) * (v - cv);
    if (d2 < best) { best = d2; qu = u; qv = v; }
  }
  const dl = pmath.hypot(qu - cu, qv - cv);
  if (dl < 1e-6 || fp === null) return dl < 1e-6 ? [cu, cv] : [cu + (qu - cu) / dl, cv + (qv - cv) / dl];
  const dx = (qu - cu) / dl, dy = (qv - cv) / dl;
  const px = -fp.ay, py = fp.ax;
  const reach = 0.5 * (Math.abs(dx * fp.ax + dy * fp.ay) * fp.w + Math.abs(dx * px + dy * py) * fp.d) + 1.1;
  return [cu + dx * reach, cv + dy * reach];
}

function straight(u0: number, v0: number, u1: number, v1: number): RegionWalkPath {
  const len = pmath.hypot(u1 - u0, v1 - v0);
  return { pts: new Float64Array([u0, v0, u1, v1]), cum: new Float64Array([0, len]), len };
}

function atPath(path: RegionWalkPath, distance: number): { u: number; v: number; heading: number } {
  if (path.pts.length < 4) return { u: path.pts[0] ?? 0, v: path.pts[1] ?? 0, heading: 0 };
  const d = Math.max(0, Math.min(path.len, distance));
  let segment = 1;
  while (segment + 1 < path.cum.length && path.cum[segment] < d) segment++;
  const d0 = path.cum[segment - 1], d1 = path.cum[segment];
  const k = d1 > d0 ? (d - d0) / (d1 - d0) : 0;
  const i = (segment - 1) * 2, j = segment * 2;
  return {
    u: path.pts[i] + (path.pts[j] - path.pts[i]) * k,
    v: path.pts[i + 1] + (path.pts[j + 1] - path.pts[i + 1]) * k,
    heading: pmath.atan2(path.pts[j + 1] - path.pts[i + 1], path.pts[j] - path.pts[i]),
  };
}

interface HeapItem { node: number; dist: number }

/** Plans and paths are pure; bounded caches affect only cost, never results. */
export class RegionLifeIndex {
  private readonly incidence: number[][];
  private readonly settlements = new Map<string, { chronicle: SettlementChronicle; parcels: number[]; plazas: number[]; civic: number[]; markets: number[]; shrines: number[]; taverns: number[] }>();
  private readonly plans = new Map<string, DaySegment[]>();
  private readonly paths = new Map<string, RegionWalkPath>();

  constructor(
    readonly graph: CityGraph,
    chronicle: RegionChronicle,
    private readonly planLimit = 4096,
    private readonly pathLimit = 4096,
  ) {
    const same = graph.regionId.face === chronicle.regionId.face && graph.regionId.x === chronicle.regionId.x
      && graph.regionId.y === chronicle.regionId.y && graph.regionId.level === chronicle.regionId.level;
    if (!same) throw new Error("RegionLifeIndex: graph and chronicle regions differ");
    this.incidence = Array.from({ length: graph.nodeU.length }, () => []);
    for (let e = 0; e < graph.edgeA.length; e++) {
      if (graph.edgeKind[e] === EDGE_TUNNEL) continue;
      this.incidence[graph.edgeA[e]].push(e); this.incidence[graph.edgeB[e]].push(e);
    }
    for (const settlement of chronicle.settlements) {
      const parcels = parcelRefs(settlement);
      this.settlements.set(settlement.settlementId, {
        chronicle: settlement, parcels,
        plazas: parcels.filter((p) => graph.parcels[p].use === USE_MARKET_SQ),
        civic: parcels.filter((p) => CIVIC_USES.has(graph.parcels[p].use)),
        markets: parcels.filter((p) => MARKET_USES.has(graph.parcels[p].use)),
        shrines: parcels.filter((p) => SHRINE_USES.has(graph.parcels[p].use)),
        taverns: parcels.filter((p) => TAVERN_USES.has(graph.parcels[p].use)),
      });
    }
  }

  get cachedPlanCount(): number { return this.plans.size; }
  get cachedPathCount(): number { return this.paths.size; }

  private settlementFor(agent: AgentRecord): { chronicle: SettlementChronicle; parcels: number[]; plazas: number[]; civic: number[]; markets: number[]; shrines: number[]; taverns: number[] } {
    const settlement = this.settlements.get(agent.settlementId);
    if (!settlement) throw new Error(`RegionLifeIndex: unknown settlement ${agent.settlementId}`);
    return settlement;
  }

  private personalSlot(
    agent: AgentRecord, anchor: string, dayIndex: number, slot: number,
    parcel: number | null, base: [number, number],
  ): [number, number] {
    const seed = seedInt(agent.id);
    const sideRoll = h01(hash32(seed, dayIndex, slot, 61)) * 2 - 1;
    const depthRoll = h01(hash32(seed, dayIndex, slot, 67));
    if (parcel !== null && anchor !== "stroll" && anchor !== "plaza") {
      const [cu, cv] = centroid(this.graph, parcel);
      let ou = base[0] - cu, ov = base[1] - cv;
      const length = pmath.hypot(ou, ov) || 1;
      ou /= length; ov /= length;
      // `base` is already the road-facing doorstep, ~1.1 m beyond the
      // footprint. Keep personal variation on that facade instead of pushing
      // residents another 5–12 m outward into the road they used to occupy.
      // Side spread widened 1.6 → 3.2 (2026-07-31): a shared venue's visitors
      // line the facade instead of piling into one shoulder-width knot.
      const side = sideRoll * 3.2;
      const depth = -0.2 + depthRoll * 0.6;
      return [base[0] + ou * depth - ov * side, base[1] + ov * depth + ou * side];
    }
    const radius = anchor === "plaza" ? 11 : 4.5;
    const angle = h01(hash32(seed, dayIndex, slot, 71)) * Math.PI * 2;
    const distance = Math.sqrt(depthRoll) * radius;
    return [base[0] + pmath.cos(angle) * distance, base[1] + pmath.sin(angle) * distance];
  }

  /** Slot anchor → [u, v, parcel]. The parcel rides into the day plan so the
   *  pose can flag INDOOR stations (P4 indoor life). Plaza/stroll anchors are
   *  outdoor by nature — their parcel is returned but their uses never map to
   *  an interior class. */
  private anchor(agent: AgentRecord, anchor: string, dayIndex: number, slot: number): [number, number, number | null] {
    const settlement = this.settlementFor(agent);
    const fallbackParcel = settlement.parcels.length > 0
      ? settlement.parcels[hash32(seedInt(agent.id), dayIndex, slot, 23) % settlement.parcels.length] : null;
    const at = (parcel: number | null, base: [number, number]): [number, number, number | null] => {
      const [u, v] = this.personalSlot(agent, anchor, dayIndex, slot, parcel, base);
      return [u, v, parcel];
    };
    if (anchor === "home") {
      const parcel = agent.homeParcel ?? fallbackParcel;
      return at(parcel, parcelDoor(this.graph, parcel));
    }
    if (anchor === "work") {
      const parcel = agent.workplaceParcel ?? fallbackParcel;
      return at(parcel, parcelDoor(this.graph, parcel));
    }
    const candidates = anchor === "plaza" ? settlement.plazas
      : anchor === "hall" ? settlement.civic
      : anchor === "market" ? settlement.markets
      : anchor === "shrine" ? settlement.shrines
      : anchor === "tavern" ? settlement.taverns
      : settlement.parcels;
    if (candidates.length === 0) {
      const parcel = agent.homeParcel ?? fallbackParcel;
      return at(parcel, parcelDoor(this.graph, parcel));
    }
    const home = agent.homeParcel === null ? [0, 0] : centroid(this.graph, agent.homeParcel);
    const ranked = candidates.map((parcel) => {
      const [u, v] = centroid(this.graph, parcel);
      return { parcel, d2: (u - home[0]) * (u - home[0]) + (v - home[1]) * (v - home[1]) };
    }).sort((a, b) => (a.d2 - b.d2) || (a.parcel - b.parcel));
    // Halls/plazas draw from a WIDER candidate pool (3 → 6, 2026-07-31): the
    // nearest-3 pick funnelled whole districts into one doorway (the crowd-
    // clump screenshot) — six near venues spread the social hour out.
    const nearCount = anchor === "stroll"
      ? Math.min(regionStrollCandidateLimit(agent), ranked.length)
      : Math.min(6, ranked.length);
    const roll = h01(hash32(seedInt(agent.id), dayIndex, slot, anchor === "stroll" ? 31 : 29));
    const parcel = ranked[Math.min(nearCount - 1, Math.floor(roll * nearCount))].parcel;
    if (anchor === "plaza") return at(parcel, centroid(this.graph, parcel));
    if (anchor === "stroll") {
      const [u, v] = centroid(this.graph, parcel);
      const node = this.nearestNode(u, v);
      const base: [number, number] = node < 0 ? [u, v] : [this.graph.nodeU[node], this.graph.nodeV[node]];
      return at(parcel, base);
    }
    return at(parcel, parcelDoor(this.graph, parcel));
  }

  dayPlan(agent: AgentRecord, dayIndex: number): DaySegment[] {
    const key = `${agent.id}|${dayIndex}`;
    const cached = this.plans.get(key);
    if (cached) { this.plans.delete(key); this.plans.set(key, cached); return cached; }
    const seed = seedInt(agent.id);
    // A demographic role is not proof of a physical job. Residents without a
    // workplace use the non-work civic/stroll rhythm instead of reporting to
    // their shared home door as a fictitious workplace.
    const schedule = agent.workplaceParcel === null && agent.role !== "wanderer" && agent.role !== "elder"
      ? NPC_SCHEDULES.wanderer : NPC_SCHEDULES[agent.role];
    const plan = schedule.map((baseSlot, i) => {
      const slot = regionScheduleVariant(agent, baseSlot);
      const jitter = (h01(hash32(seed, dayIndex, i)) * 2 - 1) * 0.02;
      const [u, v, parcel] = this.anchor(agent, slot.anchor, dayIndex, i);
      return { t0: slot.t + jitter, activity: slot.activity, u, v, parcel };
    });
    if (this.planLimit > 0) {
      this.plans.set(key, plan);
      if (this.plans.size > this.planLimit) this.plans.delete(this.plans.keys().next().value!);
    }
    return plan;
  }

  private nearestNode(u: number, v: number): number {
    let best = Infinity, node = NO_NODE;
    for (let i = 0; i < this.graph.nodeU.length; i++) {
      if (this.incidence[i].length === 0) continue;
      const d2 = (this.graph.nodeU[i] - u) * (this.graph.nodeU[i] - u) + (this.graph.nodeV[i] - v) * (this.graph.nodeV[i] - v);
      if (d2 < best) { best = d2; node = i; }
    }
    return node;
  }

  walkPath(u0: number, v0: number, u1: number, v1: number): RegionWalkPath {
    const start = this.nearestNode(u0, v0), goal = this.nearestNode(u1, v1);
    if (start < 0 || goal < 0 || start === goal) return straight(u0, v0, u1, v1);
    const forward = start < goal;
    const key = forward ? `${start}:${goal}:${u0}:${v0}:${u1}:${v1}` : `${goal}:${start}:${u1}:${v1}:${u0}:${v0}`;
    const cached = this.paths.get(key);
    if (cached) {
      if (forward) return cached;
      const n = cached.cum.length, pts = new Float64Array(cached.pts.length), cum = new Float64Array(n);
      for (let i = 0; i < n; i++) { pts[i * 2] = cached.pts[(n - 1 - i) * 2]; pts[i * 2 + 1] = cached.pts[(n - 1 - i) * 2 + 1]; cum[i] = cached.len - cached.cum[n - 1 - i]; }
      return { pts, cum, len: cached.len };
    }
    const src = forward ? start : goal, dst = forward ? goal : start;
    const dist = new Float64Array(this.graph.nodeU.length); dist.fill(Infinity); dist[src] = 0;
    const pred = new Int32Array(this.graph.nodeU.length); pred.fill(NO_NODE);
    const heap: HeapItem[] = [{ node: src, dist: 0 }];
    const less = (a: HeapItem, b: HeapItem): boolean => a.dist < b.dist || (a.dist === b.dist && a.node < b.node);
    const push = (item: HeapItem): void => {
      let i = heap.length; heap.push(item);
      while (i > 0) { const p = (i - 1) >>> 1; if (!less(item, heap[p])) break; heap[i] = heap[p]; i = p; } heap[i] = item;
    };
    const pop = (): HeapItem => {
      const root = heap[0], tail = heap.pop()!;
      if (heap.length > 0) {
        let i = 0;
        while (true) {
          let child = i * 2 + 1;
          if (child >= heap.length) break;
          if (child + 1 < heap.length && less(heap[child + 1], heap[child])) child++;
          if (!less(heap[child], tail)) break;
          heap[i] = heap[child]; i = child;
        }
        heap[i] = tail;
      }
      return root;
    };
    while (heap.length > 0) {
      const cur = pop();
      if (cur.dist !== dist[cur.node]) continue;
      if (cur.node === dst) break;
      for (const edge of this.incidence[cur.node]) {
        const next = this.graph.edgeA[edge] === cur.node ? this.graph.edgeB[edge] : this.graph.edgeA[edge];
        const nd = cur.dist + pmath.hypot(this.graph.nodeU[next] - this.graph.nodeU[cur.node], this.graph.nodeV[next] - this.graph.nodeV[cur.node]);
        if (nd < dist[next] - 1e-12) { dist[next] = nd; pred[next] = cur.node; push({ node: next, dist: nd }); }
      }
    }
    if (!(dist[dst] < Infinity)) return straight(u0, v0, u1, v1);
    const nodes: number[] = [];
    for (let node = dst, guard = 0; node !== NO_NODE && guard++ <= this.graph.nodeU.length; node = pred[node]) {
      nodes.push(node); if (node === src) break;
    }
    nodes.reverse();
    const canonicalStart: [number, number] = forward ? [u0, v0] : [u1, v1];
    const canonicalEnd: [number, number] = forward ? [u1, v1] : [u0, v0];
    const coords: number[] = [...canonicalStart];
    for (const node of nodes) coords.push(this.graph.nodeU[node], this.graph.nodeV[node]);
    coords.push(...canonicalEnd);
    const compact: number[] = [];
    for (let i = 0; i < coords.length; i += 2) {
      if (compact.length >= 2 && pmath.hypot(coords[i] - compact[compact.length - 2], coords[i + 1] - compact[compact.length - 1]) < 1e-6) continue;
      compact.push(coords[i], coords[i + 1]);
    }
    const cum = new Float64Array(compact.length / 2);
    for (let i = 1; i < cum.length; i++) cum[i] = cum[i - 1] + pmath.hypot(compact[i * 2] - compact[(i - 1) * 2], compact[i * 2 + 1] - compact[(i - 1) * 2 + 1]);
    const path = { pts: Float64Array.from(compact), cum, len: cum[cum.length - 1] ?? 0 };
    if (this.pathLimit > 0) { this.paths.set(key, path); if (this.paths.size > this.pathLimit) this.paths.delete(this.paths.keys().next().value!); }
    return forward ? path : this.walkPath(u0, v0, u1, v1);
  }

  poseAt(agent: AgentRecord, def: SystemDef, planetIdx: number, tSec: number): NpcPose {
    const period = def.planets[planetIdx].spin.periodS;
    const shift = npcClockShiftForId(agent.id, period);
    const baseFrac = directionDayFrac(def, planetIdx, [this.graph.frame.cx, this.graph.frame.cy, this.graph.frame.cz], tSec);
    const dayFracRaw = baseFrac - shift / period;
    const dayFrac = dayFracRaw - Math.floor(dayFracRaw);
    const shiftedTime = tSec - shift;
    const dayIndex = Math.floor(shiftedTime / period - dayFrac + 0.5);
    const plan = this.dayPlan(agent, dayIndex);
    let seg = plan[plan.length - 1], prev = plan[plan.length - 2] ?? seg;
    let activePlan = plan;
    let activeDayIndex = dayIndex;
    let prevDayIndex = dayIndex;
    let prevSlotIndex = plan.length - 2;
    let fromYesterday = true;
    for (let i = 0; i < plan.length; i++) if (dayFrac >= plan[i].t0) {
      seg = plan[i]; fromYesterday = false;
      if (i > 0) { prev = plan[i - 1]; prevDayIndex = dayIndex; prevSlotIndex = i - 1; }
      else {
        const yesterday = this.dayPlan(agent, dayIndex - 1);
        prev = yesterday[yesterday.length - 1];
        prevDayIndex = dayIndex - 1; prevSlotIndex = yesterday.length - 1;
      }
    }
    if (fromYesterday) {
      const yesterday = this.dayPlan(agent, dayIndex - 1);
      seg = yesterday[yesterday.length - 1]; prev = yesterday[yesterday.length - 2] ?? seg;
      activePlan = yesterday;
      activeDayIndex = dayIndex - 1;
      prevDayIndex = dayIndex - 1; prevSlotIndex = yesterday.length - 2;
    }
    const sinceStart = ((dayFrac - seg.t0 + 1) % 1) * period;
    const speed = npcWalkSpeedForId(agent.id);
    // P4 indoor life (2026-07-31): a stationary slot at a walk-in building
    // stations the resident INSIDE, near the parcel centroid. The journey
    // stays CONTINUOUS (the no-teleport invariant): an EXIT leg walks from the
    // previous slot's indoor point out to its doorstep, the road path runs
    // door-to-door, and an ENTRY leg walks doorstep → indoor point.
    const seedI = seedInt(agent.id);
    const insideFor = (s: DaySegment, day: number, idx: number): [number, number] | null => {
      const p = s.parcel ?? null;
      if (p === null || s.activity === "stroll") return null;
      const par = this.graph.parcels[p];
      if (interiorClassForUse(par.use) === null) return null;
      // Anchor INSIDE the derived building footprint (the parcel centroid can
      // sit beside the building on irregular parcels): centre + axis-aligned
      // personal jitter capped well inside the walls.
      const fp = buildingFootprint(par.poly, par.use);
      if (!fp) return null;
      const jw = (h01(hash32(seedI, day, idx, 83)) * 2 - 1) * Math.min(fp.w * 0.22, 1.6);
      const jd = (h01(hash32(seedI, day, idx, 89)) * 2 - 1) * Math.min(fp.d * 0.22, 1.6);
      return [fp.cu + fp.ax * jw - fp.ay * jd, fp.cv + fp.ay * jw + fp.ax * jd];
    };
    const segSlotIndex = Math.max(0, activePlan.indexOf(seg));
    const prevInside = insideFor(prev, prevDayIndex, prevSlotIndex);
    const exitLen = prevInside ? pmath.hypot(prev.u - prevInside[0], prev.v - prevInside[1]) : 0;
    const exitTime = exitLen / speed;
    if (prevInside && exitLen > 0.01 && sinceStart < exitTime) {
      const k = sinceStart / exitTime;
      return {
        u: prevInside[0] + (prev.u - prevInside[0]) * k,
        v: prevInside[1] + (prev.v - prevInside[1]) * k,
        activity: seg.activity, moving: true, speed,
        heading: pmath.atan2(prev.v - prevInside[1], prev.u - prevInside[0]),
        dayFrac, dayIndex, indoors: true, parcel: prev.parcel ?? undefined,
      };
    }
    const afterExit = sinceStart - exitTime;
    const path = this.walkPath(prev.u, prev.v, seg.u, seg.v);
    const walkTime = path.len / speed;
    if (path.len > 1 && afterExit < walkTime) {
      const point = atPath(path, afterExit * speed);
      return { ...point, activity: seg.activity, moving: true, speed, dayFrac, dayIndex };
    }
    // A stroll is an activity, not a road-node destination. Once the resident
    // reaches its scheduled lane, pace a second deterministic street route and
    // return to the anchor before the next slot begins. Whole ping-pong cycles
    // make both schedule boundaries continuous—no teleport at hand-off.
    if (seg.activity === "stroll") {
      const slotIndex = segSlotIndex;
      const next = activePlan[(slotIndex + 1) % activePlan.length];
      const slotFrac = (next.t0 - seg.t0 + 1) % 1;
      const dwellTime = slotFrac * period - walkTime - exitTime;
      const [pu, pv] = this.anchor(agent, "stroll", activeDayIndex, slotIndex + 97);
      const patrol = this.walkPath(seg.u, seg.v, pu, pv);
      const cycles = patrol.len > 1 && dwellTime > 0
        ? Math.floor(dwellTime * speed / (2 * patrol.len)) : 0;
      if (cycles > 0) {
        const dwell = Math.max(0, Math.min(dwellTime, afterExit - walkTime));
        const total = (dwell / dwellTime) * cycles * 2 * patrol.len;
        const leg = total % (2 * patrol.len);
        const backwards = leg > patrol.len;
        const point = atPath(patrol, backwards ? 2 * patrol.len - leg : leg);
        if (backwards) point.heading += Math.PI;
        const patrolSpeed = cycles * 2 * patrol.len / dwellTime;
        return { ...point, activity: seg.activity, moving: dwell > 0 && dwell < dwellTime, speed: patrolSpeed, dayFrac, dayIndex };
      }
    }
    const segInside = insideFor(seg, activeDayIndex, segSlotIndex);
    if (segInside) {
      // ENTRY leg: doorstep → indoor point, then stationed inside.
      const entryLen = pmath.hypot(segInside[0] - seg.u, segInside[1] - seg.v);
      const entryTime = entryLen / speed;
      const afterWalk = afterExit - walkTime;
      if (entryLen > 0.01 && afterWalk < entryTime) {
        const k = Math.max(0, afterWalk / entryTime);
        return {
          u: seg.u + (segInside[0] - seg.u) * k,
          v: seg.v + (segInside[1] - seg.v) * k,
          activity: seg.activity, moving: true, speed,
          heading: pmath.atan2(segInside[1] - seg.v, segInside[0] - seg.u),
          dayFrac, dayIndex, indoors: true, parcel: seg.parcel ?? undefined,
        };
      }
      return {
        u: segInside[0], v: segInside[1], activity: seg.activity, moving: false,
        heading: h01(hash32(seedI, activeDayIndex, segSlotIndex, 43)) * Math.PI * 2,
        speed: 0, dayFrac, dayIndex, indoors: true, parcel: seg.parcel ?? undefined,
      };
    }
    return {
      u: seg.u, v: seg.v, activity: seg.activity, moving: false,
      heading: h01(hash32(seedI, activeDayIndex, segSlotIndex, 43)) * Math.PI * 2,
      speed: 0, dayFrac, dayIndex,
      crouch: seg.activity === "work" && (agent.role === "farmer" || agent.role === "artisan"),
    };
  }
}

const NO_NODE = -1;
